import { completionStatus } from '@codemirror/autocomplete';
import { EditorView, ViewPlugin, ViewUpdate } from '@codemirror/view';
import { createElement } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { IntlProvider } from 'react-intl';

import Borders from '@dynatrace/strato-design-tokens/borders';
import BoxShadows from '@dynatrace/strato-design-tokens/box-shadows';
import Colors from '@dynatrace/strato-design-tokens/colors';
import Spacings from '@dynatrace/strato-design-tokens/spacings';

import { SuggestionsInfoSection } from './SuggestionsInfoSection.js';

const SUGGESTIONS_INFO_ATTR = 'data-dql-suggestions-info';

function findTooltipElement(): HTMLElement | null {
  return document.querySelector<HTMLElement>('.cm-tooltip-autocomplete');
}

// Strips the floating-box styling from the autocomplete tooltip container and
// reapplies it to the suggestion list (`ul`) so the suggestion list and the
// info section render as two visually independent floating boxes with a gap.
const suggestionsInfoSectionTheme = EditorView.baseTheme({
  '.cm-tooltip.cm-tooltip-autocomplete': {
    background: 'transparent !important',
    boxShadow: 'none !important',
    padding: '0 !important',
    borderRadius: '0 !important',
    display: 'flex !important',
    flexDirection: 'column',
    gap: Spacings.Size4,
    // Override the fixed `width: 500px` from the base editor config with a
    // responsive width, so the tooltip shrinks on narrow viewports. This is
    // what allows the container query in `SuggestionsInfoSection.css.ts` to
    // actually fire and reflow the info section to a column layout.
    width: `min(500px, calc(100vw - ${Spacings.Size16}))`,
  },
  '.cm-tooltip.cm-tooltip-autocomplete > ul': {
    background: Colors.Background.Surface.Default,
    borderRadius: Borders.Radius.Surface.Subdued,
    boxShadow: BoxShadows.Surface.Floating.Rest,
    padding: `${Spacings.Size4} !important`,
    flex: '0 1 auto',
    minHeight: 0,
  },
  // Pin the info section to the bottom regardless of DOM insertion order:
  // CodeMirror sometimes appends new children (e.g. the rebuilt `ul`) after our
  // section, leaving it visually above the suggestions. Setting a higher `order`
  // value forces flex to render it last while the `ul` keeps the default 0.
  [`.cm-tooltip.cm-tooltip-autocomplete > [${SUGGESTIONS_INFO_ATTR}]`]: {
    order: 1,
  },
});

const suggestionsInfoSectionPlugin = ViewPlugin.fromClass(
  class {
    private container: HTMLElement | null = null;
    private root: Root | null = null;

    update(update: ViewUpdate) {
      const status = completionStatus(update.state);

      if (status === null) {
        this.unmount();
        return;
      }

      // Defer to ensure CodeMirror has finished creating the tooltip DOM.
      queueMicrotask(() => {
        if (this.container?.isConnected) {
          return;
        }
        this.unmount();

        const tooltipEl = findTooltipElement();
        if (tooltipEl) {
          this.mount(tooltipEl);
        }
      });
    }

    private mount(tooltipEl: HTMLElement) {
      this.container = document.createElement('div');
      this.container.setAttribute(SUGGESTIONS_INFO_ATTR, '');
      tooltipEl.appendChild(this.container);

      this.root = createRoot(this.container);
      this.root.render(
        createElement(
          IntlProvider,
          { locale: 'en', defaultLocale: 'en', messages: {} },
          createElement(SuggestionsInfoSection),
        ),
      );
    }

    private unmount() {
      this.root?.unmount();
      this.root = null;
      this.container?.remove();
      this.container = null;
    }

    destroy() {
      this.unmount();
    }
  },
);

export const suggestionsInfoSectionExtension = [
  suggestionsInfoSectionTheme,
  suggestionsInfoSectionPlugin,
];
